import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  Moon, Sprout, Flame, Feather, ShieldCheck, Sparkles, HeartHandshake, ArrowRight,
} from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { CocoonMark } from "@/components/CocoonMark";
import { CinematicVideo } from "@/components/CinematicVideo";
import { CycleVertueux } from "@/components/CycleVertueux";
import { GoldenButterflies, LandingButterfly } from "@/components/GoldenButterflies";

const CHEMIN_TRAILER = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/8rkhg53s_gemini_generated_video_a24c7847.mp4";

const STEP_ICONS = [Moon, Sprout, Flame, Feather];
const PILLAR_ICONS = [ShieldCheck, Sparkles, HeartHandshake];
const COMPANION_ICONS = [Sparkles, HeartHandshake];

export default function CheminDeSoie() {
  const { t } = useTranslation();
  const pillars = t("chemin.pillars", { returnObjects: true }) || [];
  const steps = t("chemin.steps", { returnObjects: true }) || [];
  const companions = t("chemin.companions", { returnObjects: true }) || [];

  return (
    <div className="relative min-h-screen bg-background text-foreground starfield" data-testid="chemin-page">
      <GoldenButterflies />
      <LandingButterfly />
      <header className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="font-display text-xl sm:text-2xl tracking-tight whitespace-nowrap" data-testid="chemin-brand"><CocoonMark withMind /></Link>
          <div className="flex items-center gap-3 sm:gap-5">
            <LanguageSwitcher className="hidden sm:flex" />
            <Link to="/manifeste" className="hidden sm:inline text-sm text-foreground/60 hover:text-primary transition-colors">{t("nav.manifeste")}</Link>
            <Link to="/tarifs" className="hidden sm:inline text-sm text-foreground/60 hover:text-primary transition-colors">{t("nav.tarifs")}</Link>
            <Link to="/entrer" data-testid="chemin-enter-btn" className="btn-frost-orange text-sm font-semibold px-4 sm:px-6 py-2 rounded-full">{t("nav.entrer")}</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-3xl mx-auto px-6 pt-36 pb-16 text-center">
        <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
          className="text-primary/80 tracking-[0.28em] uppercase text-xs mb-6" data-testid="chemin-eyebrow">{t("chemin.eyebrow")}</motion.p>
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.1 }}
          className="font-display text-gradient-orange text-5xl sm:text-6xl tracking-tight leading-[1] mb-4" data-testid="chemin-title">{t("chemin.title")}</motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.25 }}
          className="font-display italic text-primary/70 text-xl sm:text-2xl mb-10">{t("chemin.subtitle")}</motion.p>
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.35 }}
          className="text-foreground/65 leading-relaxed text-base sm:text-lg">{t("chemin.intro")}</motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.45 }}
          className="mt-10 max-w-2xl mx-auto" data-testid="chemin-cinematic">
          <CinematicVideo fade sound soundMask src={CHEMIN_TRAILER} testId="chemin-cinematic-video" caption={t("chemin.title")} />
        </motion.div>
      </section>

      {/* Introduction — De la bulle à l'envol (cycle vertueux de la curiosité) */}
      <CycleVertueux />

      {/* Pillars */}
      <section className="max-w-5xl mx-auto px-6 py-14 sm:py-20">
        <h2 className="text-center font-display text-3xl sm:text-4xl text-gradient-orange mb-12" data-testid="chemin-pillars-title">{t("chemin.pillarsTitle")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {pillars.map((p, i) => {
            const Icon = PILLAR_ICONS[i] || ShieldCheck;
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }} data-testid={`chemin-pillar-${i}`}
                className="glass frame-pulse rounded-2xl p-8 text-center">
                <span className="w-14 h-14 rounded-full mx-auto mb-5 flex items-center justify-center bg-primary/10 border border-primary/25">
                  <Icon className="text-primary" size={26} strokeWidth={1.5} />
                </span>
                <h3 className="font-display text-2xl mb-3">{p.title}</h3>
                <p className="text-foreground/60 leading-relaxed text-sm">{p.text}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Timeline — the silk thread */}
      <section className="max-w-3xl mx-auto px-6 py-14 sm:py-20">
        <div className="text-center mb-16">
          <h2 className="font-display text-3xl sm:text-4xl text-gradient-orange mb-3" data-testid="chemin-steps-title">{t("chemin.stepsTitle")}</h2>
          <p className="text-foreground/55 max-w-xl mx-auto">{t("chemin.stepsSubtitle")}</p>
        </div>

        <div className="relative">
          {/* the thread */}
          <div className="absolute left-[27px] sm:left-[31px] top-2 bottom-2 w-px bg-gradient-to-b from-primary/10 via-primary/50 to-primary/10" aria-hidden />
          <div className="space-y-10">
            {steps.map((s, i) => {
              const Icon = STEP_ICONS[i] || Sprout;
              return (
                <motion.div key={i} initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.05 }} data-testid={`chemin-step-${i}`}
                  className="relative pl-16 sm:pl-20">
                  <span className="absolute left-0 top-0 w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center border border-primary/30 bg-background"
                    style={{ boxShadow: "0 0 24px hsl(32 90% 55% / 0.18)" }}>
                    <Icon className="text-primary" size={24} strokeWidth={1.5} />
                  </span>
                  <div className="glass rounded-2xl p-6 sm:p-7">
                    <div className="flex items-center gap-3 mb-1.5">
                      <span className="font-display text-primary/60 tabular-nums text-lg">{s.phase}</span>
                      <h3 className="font-display text-2xl sm:text-3xl">{s.name}</h3>
                    </div>
                    <p className="font-display italic text-primary/80 mb-1">{s.tagline}</p>
                    <p className="uppercase tracking-[0.18em] text-[11px] text-foreground/40 mb-5">{s.when}</p>
                    <ul className="space-y-3">
                      {(s.items || []).map((it, j) => (
                        <li key={j} className="flex items-start gap-3 text-foreground/70 leading-relaxed text-sm">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary/70 flex-shrink-0" />
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Companions */}
      <section className="max-w-4xl mx-auto px-6 py-14 sm:py-20">
        <h2 className="text-center font-display text-3xl sm:text-4xl text-gradient-orange mb-12" data-testid="chemin-companions-title">{t("chemin.companionsTitle")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {companions.map((c, i) => {
            const Icon = COMPANION_ICONS[i] || Sparkles;
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }} data-testid={`chemin-companion-${i}`}
                className="glass rounded-2xl p-8 flex flex-col items-start">
                <span className="w-12 h-12 rounded-full mb-5 flex items-center justify-center bg-primary/10 border border-primary/25">
                  <Icon className="text-primary" size={22} strokeWidth={1.5} />
                </span>
                <h3 className="font-display text-2xl mb-1">{c.name}</h3>
                <p className="text-primary/70 text-sm uppercase tracking-[0.16em] mb-4">{c.role}</p>
                <p className="text-foreground/65 leading-relaxed">{c.text}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-6 py-20 text-center">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
          className="glass frame-pulse rounded-3xl px-8 py-14" data-testid="chemin-cta">
          <h2 className="font-display text-3xl sm:text-4xl text-gradient-orange mb-4">{t("chemin.ctaTitle")}</h2>
          <p className="text-foreground/65 leading-relaxed max-w-xl mx-auto mb-9">{t("chemin.ctaText")}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/entrer" data-testid="chemin-cta-start" className="btn-frost-orange px-8 py-4 rounded-full font-semibold inline-flex items-center justify-center gap-2">
              {t("chemin.ctaButton")} <ArrowRight size={18} />
            </Link>
            <Link to="/tarifs" data-testid="chemin-cta-pricing" className="glass px-8 py-4 rounded-full font-semibold text-foreground/75 hover:text-primary transition-colors inline-flex items-center justify-center">
              {t("chemin.ctaPricing")}
            </Link>
          </div>
        </motion.div>
      </section>

      <footer className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-4 text-sm text-foreground/40">
          <Link to="/" className="font-display text-lg"><CocoonMark letterClassName="text-foreground/60" withMind /></Link>
          <span>{t("landing.footerTagline")}</span>
        </div>
      </footer>
    </div>
  );
}
